import { useRef, useEffect, useState, type ReactNode } from "react";

interface InfoTooltipProps {
  children: ReactNode;
  className?: string;
}

export default function InfoTooltip({
  children,
  className = "",
}: InfoTooltipProps) {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const [positionClasses, setPositionClasses] = useState("");

  useEffect(() => {
    const adjustTooltipPosition = () => {
      if (!tooltipRef.current || !parentRef.current) return;

      const parent = parentRef.current;
      const tooltip = tooltipRef.current;
      const parentRect = parent.getBoundingClientRect();
      const tooltipRect = tooltip.getBoundingClientRect();
      const drawerPanel = document.getElementById("drawer-panel");

      let classes = "";

      if (drawerPanel) {
        const drawerRect = drawerPanel.getBoundingClientRect();

        const spaceAbove = parentRect.top - drawerRect.top;
        const spaceBelow = drawerRect.bottom - parentRect.bottom;
        const tooltipHeight = tooltipRect.height || 100;

        if (
          spaceAbove < tooltipHeight + 20 &&
          spaceBelow > tooltipHeight + 20
        ) {
          classes += "tooltip-below ";
        }

        const parentCenterX = parentRect.left + parentRect.width / 2;
        const tooltipWidth = tooltipRect.width || 288;
        const tooltipHalfWidth = tooltipWidth / 2;

        if (parentCenterX - tooltipHalfWidth < drawerRect.left + 10) {
          classes += "tooltip-left ";
        } else if (parentCenterX + tooltipHalfWidth > drawerRect.right - 10) {
          classes += "tooltip-right ";
        }
      }

      setPositionClasses(classes.trim());
    };

    const handleMouseEnter = () => {
      setTimeout(adjustTooltipPosition, 10);
    };

    const parent = parentRef.current;
    if (parent) {
      parent.addEventListener("mouseenter", handleMouseEnter);
      window.addEventListener("resize", adjustTooltipPosition);

      return () => {
        parent.removeEventListener("mouseenter", handleMouseEnter);
        window.removeEventListener("resize", adjustTooltipPosition);
      };
    }
  }, []);

  return (
    <div
      ref={parentRef}
      className={`relative md:inline-block group hidden ${className}`}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 cursor-pointer"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <div
        ref={tooltipRef}
        className={`smart-tooltip ${positionClasses} absolute h-auto w-72 z-20 invisible group-hover:visible bg-[#DDA248] text-white text-xs rounded p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300`}
      >
        {children}
        <div className="tooltip-arrow absolute border-4 border-transparent"></div>
      </div>
    </div>
  );
}
